import { supabase } from './supabase';

/*
  A setting kept against one person, rather than against the company or the
  browser.

  Written 5 September 2026 to finish "Count entry becomes one screen".

  **The Item Locations switch was forgotten every single time.** It lived in the
  screen and was written nowhere at all, so it started off on every open. Not
  every rep records where a box sits on a shelf, but the ones who do had to
  switch it on again for every location, every quarter, and nothing anywhere
  remembered they wanted it.

  **Not `app_settings`**, which is one row per company: one rep turning the
  switch on would have turned it on for everybody. And **not the browser**,
  because a rep counts on a phone in the morning and checks the sheet on a
  laptop in the afternoon, and a setting that only one of those knows about is
  half a setting.
*/

export const ITEM_LOCATIONS = 'count_entry.item_locations';

/*
  Read one setting. **A missing row is not an error**: it means this person has
  never touched the switch, so they get the default, and the caller says what
  the default is rather than this guessing.
*/
export async function readPreference(userId, key, fallback = null) {
  if (!userId) return fallback;
  const { data, error } = await supabase
    .from('user_preferences')
    .select('value')
    .eq('user_id', userId)
    .eq('key', key)
    .maybeSingle();
  if (error || !data) return fallback;
  return data.value;
}

/*
  Write one setting, and say whether it was actually written.

  **A save is proved by reading the row back, never by the absence of an
  error**, which is this project's own hardest-learned rule: marking a
  notification read had no rule permitting it, so the database changed no rows,
  raised nothing, and answered "fine". This returns false when the write did not
  land, so the screen can put itself back rather than showing a switch in a
  position the database does not agree with.
*/
export async function writePreference(userId, key, value) {
  if (!userId) return false;
  const { data, error } = await supabase
    .from('user_preferences')
    .upsert({ user_id: userId, key, value: String(value), updated_at: new Date().toISOString() },
            { onConflict: 'user_id,key' })
    .select('value')
    .maybeSingle();
  if (error) return false;
  return data?.value === String(value);
}
